/**
 * CauseListPolicyCard — v2.19.4.
 *
 * Two responsibilities:
 *   1. Manual "Sync cause list now" button (routes through the
 *      Hearing-Anchored · Causelist-First + Delta engine).
 *   2. Delivery preferences — per-tenant on/off toggles for the WhatsApp
 *      and email PDF dispatch that fires after every refresh (manual +
 *      automated daily 19:30 IST T-1 cron).
 *
 * Backend:
 *   POST   /api/cause-list/refresh
 *   GET    /api/cause-list/prefs/delivery
 *   PATCH  /api/cause-list/prefs/delivery
 *
 * Only firm_owner / managing_partner can toggle the delivery prefs — the
 * PATCH endpoint 403s otherwise. Non-owners see the toggles read-only.
 */
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Scale, CalendarCheck, Loader2, MessageCircle, Mail } from "lucide-react";
import { api, apiError } from "@/lib/api";

export default function CauseListPolicyCard() {
    const [syncing, setSyncing] = useState(false);
    const [lastSync, setLastSync] = useState(null);
    const [prefs, setPrefs] = useState(null);
    const [savingPref, setSavingPref] = useState(null); // "whatsapp" | "email"

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/cause-list/prefs/delivery");
                setPrefs(data.prefs);
            } catch { /* non-fatal — widget just stays without toggles */ }
        })();
    }, []);

    const togglePref = async (channel) => {
        if (savingPref) return;
        const next = { ...prefs, [channel]: !prefs[channel] };
        setSavingPref(channel);
        setPrefs(next);                                 // optimistic
        try {
            const { data } = await api.patch("/cause-list/prefs/delivery",
                { [channel]: next[channel] });
            setPrefs(data.prefs);
            toast.success(`${channel === "whatsapp" ? "WhatsApp" : "Email"} delivery ${next[channel] ? "enabled" : "paused"}`);
        } catch (e) {
            setPrefs(prefs);                            // rollback
            toast.error(apiError(e));
        } finally { setSavingPref(null); }
    };

    const syncNow = async () => {
        setSyncing(true);
        try {
            const { data } = await api.post("/cause-list/refresh", {}, { timeout: 5 * 60 * 1000 });
            const r = data?.result || {};
            setLastSync(r);
            if (r.skipped) {
                toast.message("Cause-list sync skipped", { description: r.reason || "—" });
            } else {
                const probed = r.calls_made ?? 0;
                const escalated = r.escalated ?? 0;
                toast.success(probed > 0
                    ? `Synced — probed ${probed} case${probed === 1 ? "" : "s"} · ${escalated} refreshed with new date`
                    : "Synced — no hearings scheduled for tomorrow");
            }
        } catch (e) { toast.error(apiError(e)); }
        finally { setSyncing(false); }
    };

    return (
        <div className="bg-muted border border-border p-6 space-y-4" data-testid="causelist-policy-card">
            <div className="flex items-center gap-2 mb-1">
                <Scale className="w-4 h-4 text-primary" strokeWidth={1.5}/>
                <div className="overline">Cause-list sync</div>
            </div>
            <p className="text-xs text-muted-foreground">
                Pulls tomorrow&apos;s cause-list for every case in your firm that has a hearing on record. Uses the
                <span className="font-mono text-primary"> cnr_causelist_batch </span> API — <strong>₹0.60/case</strong> tenant-billed
                (100 cases per batch call). Runs automatically at <strong>19:30 IST</strong> every day; use the button below to trigger it manually.
            </p>

            <div className="flex flex-wrap items-center gap-3">
                <button type="button" onClick={syncNow} disabled={syncing}
                    data-testid="causelist-sync-now-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-primary text-primary text-xs uppercase tracking-[0.16em] hover:bg-primary hover:text-primary-foreground disabled:opacity-30">
                    {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <CalendarCheck className="w-3.5 h-3.5" strokeWidth={1.5}/>}
                    {syncing ? "Syncing…" : "Sync cause list now"}
                </button>
                {lastSync && !lastSync.skipped && (
                    <span className="text-[11px] text-muted-foreground" data-testid="causelist-last-sync-summary">
                        Last manual sync · {lastSync.calls_made ?? 0} probed ·
                        {" "}{lastSync.escalated ?? 0} refreshed ·
                        {" "}target {lastSync.target_date || "tomorrow"}
                    </span>
                )}
                {lastSync && lastSync.skipped && (
                    <span className="text-[11px] text-yellow-500" data-testid="causelist-last-sync-skipped">
                        Skipped: {lastSync.reason || "—"}
                    </span>
                )}
            </div>

            {/* v2.19.4 · Delivery preferences */}
            {prefs && (
                <div className="border-t border-border pt-4 mt-4" data-testid="causelist-delivery-prefs">
                    <div className="flex items-center justify-between mb-2">
                        <div className="overline text-[10px]">PDF delivery preferences</div>
                        <span className="text-[10px] text-muted-foreground">Fires after every refresh</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <PrefRow icon={MessageCircle} label="WhatsApp"
                            active={prefs.whatsapp} saving={savingPref === "whatsapp"}
                            onToggle={() => togglePref("whatsapp")}
                            testid="pref-whatsapp"
                            hint={prefs.whatsapp ? "Sends PDF to owner's phone" : "Paused"}/>
                        <PrefRow icon={Mail} label="Email"
                            active={prefs.email} saving={savingPref === "email"}
                            onToggle={() => togglePref("email")}
                            testid="pref-email"
                            hint={prefs.email ? "Sends PDF to owner's email" : "Paused"}/>
                    </div>
                </div>
            )}
        </div>
    );
}

function PrefRow({ icon: Icon, label, active, saving, onToggle, testid, hint }) {
    return (
        <button type="button" onClick={onToggle} disabled={saving}
            data-testid={testid}
            className={`flex items-center gap-3 p-3 border transition text-left disabled:opacity-50
                ${active ? "border-primary/40 bg-primary/5" : "border-border"}`}>
            <Icon className={`w-4 h-4 shrink-0 ${active ? "text-primary" : "text-muted-foreground"}`} strokeWidth={1.5}/>
            <div className="flex-1 min-w-0">
                <div className={`text-xs ${active ? "text-foreground" : "text-muted-foreground"}`}>{label}</div>
                <div className="text-[10px] text-muted-foreground truncate">{hint}</div>
            </div>
            <div className={`w-9 h-5 rounded-full transition relative shrink-0
                ${active ? "bg-primary" : "bg-border"}`}>
                <div className={`w-4 h-4 bg-background rounded-full absolute top-0.5 transition-transform
                    ${active ? "translate-x-4" : "translate-x-0.5"}`}/>
            </div>
        </button>
    );
}
